import React from 'react';
import ReactDOM from 'react-dom';
import API from '../api';
import * as _i from 'shared/utils/collections';

import TopBar from './TopBar';
import ChatBubble from './ChatBubble';

const ANNOUNCEMENTS_CACHE_KEY = 'announcement_state_v1';
const ANNOUNCEMENTS_TTL_MS = 300000;

const getTimestamp = (value: unknown): number => {
  const parsed = +new Date(value as string | number | Date);
  return Number.isFinite(parsed) ? parsed : 0;
};

const readSessionAnnouncementsState = (): Record<string, any> => {
  try {
    const raw = window.sessionStorage.getItem(ANNOUNCEMENTS_CACHE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch (_e) {
    return {};
  }
};

const TopBarContainer = ({ children }: { children: React.ReactNode }) => {
  return ReactDOM.createPortal(
    children,
    document.getElementById('a-top-bar-container')!
  );
};

const ChatBubbleContainer = ({ children }: { children: React.ReactNode }) => {
  return ReactDOM.createPortal(
    children,
    document.getElementById('a-chat-bubble-container')!
  );
};

class Announce extends React.PureComponent<any, any> {
  _isMounted = false;

  constructor(props: Record<string, any>) {
    super(props);
    this.sendAnnouncementEvent = this.sendAnnouncementEvent.bind(this);

    const windowState = window.__a_announcements_state || {};
    const sessionState = readSessionAnnouncementsState();
    const bootState = getTimestamp(windowState.lastFetch) > 0 ? windowState : sessionState;

    this.state = {
      topBars: bootState.topBars || [],
      chatBubbles: bootState.chatBubbles || [],
      lastFetch: getTimestamp(bootState.lastFetch),
      localReactions: JSON.parse(
        window.localStorage.getItem('announcement_reactions') || '[]'
      ),
    };
  }

  componentDidMount() {
    this._isMounted = true;

    const lastFetch = Number(this.state.lastFetch || 0);
    if (!lastFetch || Date.now() - lastFetch > ANNOUNCEMENTS_TTL_MS) {
      this.getAnnouncements();
    }
  }

  componentWillUnmount() {
    this.persistAnnouncementsState(this.state);
    this._isMounted = false;
  }

  persistAnnouncementsState(state: Record<string, any>) {
    const cacheState = {
      topBars: state.topBars || [],
      chatBubbles: state.chatBubbles || [],
      lastFetch: getTimestamp(state.lastFetch),
    };

    window.__a_announcements_state = cacheState;

    try {
      window.sessionStorage.setItem(
        ANNOUNCEMENTS_CACHE_KEY,
        JSON.stringify(cacheState)
      );
    } catch (_e) {
      // ignore storage issues
    }
  }

  getAnnouncements() {
    const { protocol, host, site } = this.props;
    API.getAnnouncements(protocol, host, site).then(
      (announcements: any) => {
        if (this._isMounted)
          this.setState(
            {
              topBars: (announcements || []).filter(
                (a: Record<string, any>) => a.announcement_type === 'bar'
              ),
              chatBubbles: (announcements || []).filter(
                (a: Record<string, any>) => a.announcement_type === 'bubble'
              ),
              lastFetch: Date.now(),
            },
            () => this.persistAnnouncementsState(this.state)
          );
      },
      (e) => console.log('API Error', e)
    );
  }

  sendAnnouncementEvent(uuid: string, name: string) {
    const { protocol, host, site } = this.props;
    API.sendAnnouncementEvent(protocol, host, uuid, name);
    this.addEventToState(uuid, name);
  }

  addEventToState(uuid: string, name: string) {
    const { topBars, chatBubbles, localReactions } = this.state;

    let _state: Record<string, any> = {};
    let aType = 'topBars';
    let announcement = _i.findByProp(topBars, 'uuid', uuid);

    if (!announcement) {
      aType = 'chatBubbles';
      announcement = _i.findByProp(chatBubbles, 'uuid', uuid);
      let localAnnouncement: any = _i.findByProp(localReactions as any[], 'uuid', uuid) || { uuid, events: [] };

      if ((localAnnouncement.events || []).indexOf(name) < 0) {
        let localEvents = [...(localAnnouncement.events || []), name];
        _state['localReactions'] = _i.updateByProp(
          localReactions,
          'uuid',
          uuid,
          { uuid, events: localEvents }
        );
        window.localStorage.setItem(
          'announcement_reactions',
          JSON.stringify(_state['localReactions'])
        );
      }
    }

    let reaction_counts = { ...(announcement as any)?.reaction_counts };
    if (Object.keys(reaction_counts || {}).indexOf(name) > -1)
      reaction_counts[name]++;
    _state[aType] = _i.updateByProp(this.state[aType] as any[], 'uuid', uuid, {
      reaction_counts,
    });

    this.setState(_state);
  }

  render() {
    return (
      <div className="a-announcements">
        <TopBarContainer>
          <TopBar
            announcements={this.state.topBars}
            sendAnnouncementEvent={this.sendAnnouncementEvent}
            localReactions={this.state.localReactions}
          />
        </TopBarContainer>
        <ChatBubbleContainer>
          <ChatBubble
            announcements={this.state.chatBubbles}
            sendAnnouncementEvent={this.sendAnnouncementEvent}
            localReactions={this.state.localReactions}
          />
        </ChatBubbleContainer>
      </div>
    );
  }
}

export default Announce;
